import { useState, useEffect, useCallback, ReactNode } from 'react';
import toast from 'react-hot-toast';
import {
	DocumentTextIcon,
	MagnifyingGlassIcon,
	ExclamationTriangleIcon,
	CheckCircleIcon,
	ClockIcon,
	XCircleIcon,
} from '@heroicons/react/24/outline';
import AppLayout from '../../components/layout/AppLayout';
import { Alert, AppLoader, Button, Modal, Spinner } from '../../components/ui';
import { providerProfileApi, ApiError } from '../../api';
import useProviderProfileStore from '../../stores/providerProfileStore';
import type {
	ProviderTcr,
	ProviderTcrResponse,
	ProviderTcrStaffOption,
} from '../../types/providerTcr';

// ─── Constants ──────────────────────────────────────────────────

const STATUS_STYLES: Record<
	string,
	{ border: string; badge: string; banner: string; icon: string }
> = {
	PENDING: {
		border: 'border-l-amber-500',
		badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
		banner: 'bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800',
		icon: 'bg-amber-100 dark:bg-amber-900/40',
	},
	APPROVED: {
		border: 'border-l-emerald-500',
		badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
		banner: 'bg-emerald-50 border border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800',
		icon: 'bg-emerald-100 dark:bg-emerald-900/40',
	},
	REJECTED: {
		border: 'border-l-red-500',
		badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
		banner: 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800',
		icon: 'bg-red-100 dark:bg-red-900/40',
	},
};

const STATUS_OPTIONS = [
	{ value: 'ALL', label: 'All Status' },
	{ value: 'PENDING', label: 'Pending' },
	{ value: 'APPROVED', label: 'Approved' },
	{ value: 'REJECTED', label: 'Rejected' },
];

// ─── Helpers ────────────────────────────────────────────────────

const formatCurrency = (amount: number): string => {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
};

const formatDate = (dateStr: string): string => {
	try {
		const date = new Date(dateStr);
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		});
	} catch {
		return dateStr;
	}
};

const formatTaskType = (taskType: string): string => {
	return taskType.charAt(0).toUpperCase() + taskType.slice(1).toLowerCase();
};

const getDateParts = (dateStr: string): { day: string; month: string } => {
	try {
		const date = new Date(dateStr);
		return {
			day: date.getDate().toString(),
			month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
		};
	} catch {
		return { day: '-', month: '-' };
	}
};

const getStatusStyle = (status: string) => STATUS_STYLES[status] || STATUS_STYLES.PENDING;

const StatusIcon = ({ status, className }: { status: string; className?: string }) => {
	const iconClass = className || 'w-5 h-5';
	switch (status) {
		case 'APPROVED':
			return <CheckCircleIcon className={iconClass} />;
		case 'REJECTED':
			return <XCircleIcon className={iconClass} />;
		default:
			return <ClockIcon className={iconClass} />;
	}
};

// ─── TCR Detail Modal ───────────────────────────────────────────

interface TCRDetailModalProps {
	tcr: ProviderTcr | null;
	onClose: () => void;
	onApprove: (id: number, bonus: number, adjustment: number) => void;
	onReject: (id: number, notes: string) => void;
	isApproving: boolean;
	isRejecting: boolean;
}

const TCRDetailModal = ({
	tcr,
	onClose,
	onApprove,
	onReject,
	isApproving,
	isRejecting,
}: TCRDetailModalProps) => {
	const [showRejectForm, setShowRejectForm] = useState(false);
	const [rejectionNotes, setRejectionNotes] = useState('');
	const [bonusInput, setBonusInput] = useState('');
	const [adjustmentInput, setAdjustmentInput] = useState('');
	const [photos, setPhotos] = useState<string[]>([]);
	const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);

	// Reset forms when modal opens/closes or TCR changes
	useEffect(() => {
		setShowRejectForm(false);
		setRejectionNotes('');
		setBonusInput('');
		setAdjustmentInput('');
		setPhotos([]);
	}, [tcr]);

	// Lazy-load photos when modal opens
	useEffect(() => {
		if (!tcr || !tcr.hasPhotos) return;
		let cancelled = false;
		setIsLoadingPhotos(true);
		providerProfileApi
			.getTcrPhotos(tcr.id)
			.then(data => {
				if (!cancelled) setPhotos(data);
			})
			.catch(() => {
				/* silently fail — photos are non-critical */
			})
			.finally(() => {
				if (!cancelled) setIsLoadingPhotos(false);
			});
		return () => {
			cancelled = true;
		};
	}, [tcr]);

	if (!tcr) return null;

	const style = getStatusStyle(tcr.status);
	const isPending = tcr.status === 'PENDING';

	const handleReject = () => {
		if (!rejectionNotes.trim()) {
			toast.error('Please provide a reason for rejection');
			return;
		}
		onReject(tcr.id, rejectionNotes.trim());
	};

	const headerContent = (
		<div
			className="px-6 py-5 flex items-center gap-4"
			style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
		>
			<div className="flex-1 min-w-0">
				<h2 className="text-lg font-bold text-white truncate">Task Completion Report</h2>
				<p className="text-sm text-white/80 mt-0.5 truncate">
					{tcr.serviceName} &middot; {formatDate(tcr.appointmentDateTime)}
				</p>
			</div>
		</div>
	);

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			title=""
			size="xl"
			headerContent={headerContent}
			panelClassName="max-h-[90vh] flex flex-col"
			bodyClassName="!p-0 flex-1 flex flex-col min-h-0"
		>
			<div className="flex-1 overflow-y-auto p-6 space-y-6">
				{/* Status Banner */}
				<div className={`flex items-center gap-3 p-4 rounded-xl ${style.banner}`}>
					<div
						className={`w-10 h-10 rounded-lg flex items-center justify-center ${style.icon}`}
					>
						<StatusIcon status={tcr.status} className="w-5 h-5" />
					</div>
					<div>
						<p className="font-semibold text-gray-900 dark:text-white">
							{tcr.status === 'APPROVED'
								? 'Approved'
								: tcr.status === 'REJECTED'
									? 'Rejected'
									: 'Pending Review'}
						</p>
						<p className="text-sm text-gray-500 dark:text-gray-400">
							Submitted {formatDate(tcr.submittedAt)}
						</p>
					</div>
				</div>

				{/* Staff Information */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Staff Information
					</h3>
					<div className="grid grid-cols-2 gap-3">
						<div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
							<p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
								Staff Name
							</p>
							<p className="text-sm font-semibold text-gray-900 dark:text-white">
								{tcr.staffName}
							</p>
						</div>
						<div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
							<p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
								Role
							</p>
							<p className="text-sm font-semibold text-gray-900 dark:text-white">
								{tcr.staffRole}
							</p>
						</div>
					</div>
				</div>

				{/* Service Details */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Service Details
					</h3>
					<div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
						{[
							{ label: 'Client', value: tcr.customerName },
							{ label: 'Service', value: tcr.serviceName },
							{ label: 'Size', value: tcr.size || '-' },
							{ label: 'Length', value: tcr.hairLength || '-' },
							{
								label: 'Duration',
								value: tcr.durationMinutes ? `${tcr.durationMinutes} mins` : '-',
							},
							{ label: 'Service Price', value: formatCurrency(tcr.amount) },
						].map(item => (
							<div
								key={item.label}
								className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg"
							>
								<p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
									{item.label}
								</p>
								<p className="text-sm font-semibold text-gray-900 dark:text-white">
									{item.value}
								</p>
							</div>
						))}
					</div>
				</div>

				{/* Tasks Performed */}
				{tcr.taskCompletions && tcr.taskCompletions.length > 0 && (
					<div>
						<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
							Tasks Performed
						</h3>
						<div className="space-y-3">
							{tcr.taskCompletions.map((task, idx) => {
								const pctDisplay = Math.round(task.completionPct * 100);
								const selectedTask = tcr.selectedTasks.find(
									st => st.taskType === task.taskType
								);
								const taskCommission = selectedTask
									? selectedTask.price * task.completionPct
									: 0;
								return (
									<div
										key={idx}
										className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg"
									>
										<div className="flex items-center gap-3">
											<div
												className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
												style={{
													background:
														'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
												}}
											>
												{task.taskType.charAt(0)}
											</div>
											<div>
												<p className="font-semibold text-gray-900 dark:text-white">
													{formatTaskType(task.taskType)}
												</p>
												<p className="text-sm text-gray-500 dark:text-gray-400">
													{pctDisplay}% completion
												</p>
											</div>
										</div>
										<span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
											{formatCurrency(taskCommission)}
										</span>
									</div>
								);
							})}
						</div>
					</div>
				)}

				{/* Materials Used */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Materials Used
					</h3>
					{tcr.materials && tcr.materials.length > 0 ? (
						<div className="space-y-2">
							{tcr.materials.map((material, idx) => (
								<div
									key={idx}
									className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg"
								>
									<div>
										<p className="text-sm font-medium text-gray-900 dark:text-white">
											{material.materialName ||
												formatTaskType(material.materialType)}
										</p>
										{material.length && (
											<p className="text-xs text-gray-500 dark:text-gray-400">
												Length: {material.length}
											</p>
										)}
									</div>
									<div className="text-right">
										<p className="text-sm font-medium text-gray-900 dark:text-white">
											{material.packs} pack{material.packs !== 1 ? 's' : ''}
										</p>
										<p className="text-xs text-gray-500 dark:text-gray-400">
											{formatCurrency(material.price)}
										</p>
									</div>
								</div>
							))}
						</div>
					) : (
						<p className="text-sm text-gray-400 dark:text-gray-500 italic">
							No materials used
						</p>
					)}
				</div>

				{/* Tip */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Tip from Customer
					</h3>
					{tcr.tip > 0 ? (
						<div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-4 rounded-lg">
							<span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
								{formatCurrency(tcr.tip)}
							</span>
						</div>
					) : (
						<p className="text-sm text-gray-400 dark:text-gray-500 italic">
							No tip received
						</p>
					)}
				</div>

				{/* Work Notes */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Work Notes
					</h3>
					{tcr.notes ? (
						<div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border-l-3 border-[#667eea]">
							<p className="text-sm text-gray-700 dark:text-gray-300">{tcr.notes}</p>
						</div>
					) : (
						<p className="text-sm text-gray-400 dark:text-gray-500 italic">
							No work notes
						</p>
					)}
				</div>

				{/* Photos */}
				{tcr.hasPhotos && (
					<div>
						<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
							Completed Work Photos
						</h3>
						{isLoadingPhotos ? (
							<div className="flex items-center justify-center py-8">
								<svg
									className="animate-spin h-6 w-6 text-purple-500"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										className="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										strokeWidth="4"
									/>
									<path
										className="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
									/>
								</svg>
								<span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
									Loading photos…
								</span>
							</div>
						) : photos.length > 0 ? (
							<div className="grid grid-cols-2 gap-3">
								{photos.map((photo, idx) => (
									<div
										key={idx}
										className="rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 aspect-square"
									>
										<img
											src={photo}
											alt={`Work photo ${idx + 1}`}
											className="w-full h-full object-cover"
										/>
									</div>
								))}
							</div>
						) : (
							<p className="text-sm text-gray-400 dark:text-gray-500 italic">
								Failed to load photos
							</p>
						)}
					</div>
				)}

				{/* Commission Summary */}
				<div>
					<h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
						Commission Summary
					</h3>
					{(() => {
						// For pending: use live input values; for approved/rejected: use saved values
						const bonus = isPending ? parseFloat(bonusInput) || 0 : (tcr.bonus ?? 0);
						const adjustment = isPending
							? parseFloat(adjustmentInput) || 0
							: (tcr.adjustment ?? 0);
						const baseCommission = tcr.totalEarnings;
						const totalCommission = baseCommission + bonus - adjustment;

						return (
							<div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-[0_0_12px_rgba(0,0,0,0.08)]">
								{/* Base Commission */}
								<div className="flex justify-between items-center pb-4 mb-4 border-b border-gray-200 dark:border-gray-700">
									<span className="text-[0.95rem] text-gray-600 dark:text-gray-400">
										Base Commission
									</span>
									<span className="text-xl font-bold text-gray-900 dark:text-white">
										{formatCurrency(baseCommission)}
									</span>
								</div>

								{/* Adjustments */}
								<div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4">
									{/* Bonus row */}
									<div className="flex justify-between items-center py-2.5">
										<div className="flex items-center gap-2.5">
											<span className="w-7 h-7 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-sm font-bold">
												+
											</span>
											<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
												Bonus
											</span>
										</div>
										{isPending ? (
											<div className="flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5">
												<span className="text-gray-400 font-semibold mr-1 text-sm">
													$
												</span>
												<input
													type="number"
													min="0"
													step="1"
													placeholder="0.00"
													value={bonusInput}
													onChange={e => setBonusInput(e.target.value)}
													className="bg-transparent border-none text-gray-900 dark:text-white text-right font-semibold w-20 focus:outline-none text-sm"
												/>
											</div>
										) : (
											<span className="font-semibold text-gray-900 dark:text-white">
												{formatCurrency(bonus)}
											</span>
										)}
									</div>

									{/* Divider */}
									<div className="border-t border-gray-200 dark:border-gray-600" />

									{/* Adjustment row */}
									<div className="flex justify-between items-center py-2.5">
										<div className="flex items-center gap-2.5">
											<span className="w-7 h-7 rounded-lg bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 flex items-center justify-center text-sm font-bold">
												&minus;
											</span>
											<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
												Adjustment
											</span>
										</div>
										{isPending ? (
											<div className="flex items-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1.5">
												<span className="text-gray-400 font-semibold mr-1 text-sm">
													$
												</span>
												<input
													type="number"
													min="0"
													step="1"
													placeholder="0.00"
													value={adjustmentInput}
													onChange={e =>
														setAdjustmentInput(e.target.value)
													}
													className="bg-transparent border-none text-gray-900 dark:text-white text-right font-semibold w-20 focus:outline-none text-sm"
												/>
											</div>
										) : (
											<span className="font-semibold text-gray-900 dark:text-white">
												{formatCurrency(adjustment)}
											</span>
										)}
									</div>
								</div>

								{/* Total Commission to Pay */}
								<div className="flex justify-between items-center pt-4 border-t-2 border-gray-200 dark:border-gray-700">
									<span className="text-[0.95rem] font-semibold text-gray-900 dark:text-white">
										Total Commission to Pay
									</span>
									<span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
										{formatCurrency(totalCommission)}
									</span>
								</div>
							</div>
						);
					})()}
				</div>

				{/* Review Notes (rejected only) */}
				{tcr.status === 'REJECTED' && tcr.reviewNotes && (
					<div>
						<h3 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-3 flex items-center gap-2">
							<ExclamationTriangleIcon className="w-4 h-4" />
							Review Notes
						</h3>
						<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg">
							<p className="text-sm text-red-700 dark:text-red-300">
								{tcr.reviewNotes}
							</p>
						</div>
					</div>
				)}

				{/* Rejection Form (inline, shown when reject is clicked) */}
				{isPending && showRejectForm && (
					<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-xl">
						<label className="block font-semibold text-red-700 dark:text-red-400 mb-2 text-sm">
							Reason for Rejection *
						</label>
						<textarea
							value={rejectionNotes}
							onChange={e => setRejectionNotes(e.target.value)}
							placeholder="Please explain why this TCR is being rejected..."
							className="w-full p-3 border border-red-200 dark:border-red-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-red-400 resize-vertical min-h-[80px]"
							disabled={isRejecting}
						/>
						<div className="flex gap-3 mt-3">
							<Button
								variant="primary"
								size="sm"
								onClick={handleReject}
								isLoading={isRejecting}
								className="!bg-red-600 hover:!bg-red-700"
							>
								Confirm Rejection
							</Button>
							<Button
								variant="secondary"
								size="sm"
								onClick={() => {
									setShowRejectForm(false);
									setRejectionNotes('');
								}}
								disabled={isRejecting}
							>
								Cancel
							</Button>
						</div>
					</div>
				)}
			</div>

			{/* Footer Actions */}
			{isPending && !showRejectForm && (
				<div className="border-t border-gray-200 dark:border-gray-700 p-4 flex gap-3">
					<Button
						variant="secondary"
						className="flex-1 !border-2 !border-red-300 dark:!border-red-700 !text-red-600 dark:!text-red-400 !bg-transparent hover:!bg-red-50 dark:hover:!bg-red-900/20"
						onClick={() => setShowRejectForm(true)}
						disabled={isApproving}
					>
						<XCircleIcon className="w-4 h-4 mr-2" />
						Reject
					</Button>
					<Button
						variant="primary"
						className="flex-1 !bg-emerald-600 hover:!bg-emerald-700"
						onClick={() =>
							onApprove(
								tcr.id,
								parseFloat(bonusInput) || 0,
								parseFloat(adjustmentInput) || 0
							)
						}
						isLoading={isApproving}
					>
						<CheckCircleIcon className="w-4 h-4 mr-2" />
						Approve
					</Button>
				</div>
			)}
		</Modal>
	);
};

// ─── Stats Card ─────────────────────────────────────────────────

interface StatCardProps {
	value: string | number;
	label: string;
	borderColor: string;
	iconBg: string;
	icon: ReactNode;
}

const StatCard = ({ value, label, borderColor, iconBg, icon }: StatCardProps) => (
	<div className={`bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border-l-4 ${borderColor}`}>
		<div className="flex items-center justify-between">
			<div>
				<p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{label}</p>
			</div>
			<div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconBg}`}>
				{icon}
			</div>
		</div>
	</div>
);

// ─── Main TCR Approvals Page ────────────────────────────────────

const ProviderTCRApprovals = () => {
	const { selectedLocationId } = useProviderProfileStore();

	// Filter state
	const [statusFilter, setStatusFilter] = useState('ALL');
	const [staffFilter, setStaffFilter] = useState('');
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [dateFrom, setDateFrom] = useState('');
	const [dateTo, setDateTo] = useState('');

	// Data state
	const [data, setData] = useState<ProviderTcrResponse | null>(null);
	const [staffOptions, setStaffOptions] = useState<ProviderTcrStaffOption[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Modal state
	const [selectedTcr, setSelectedTcr] = useState<ProviderTcr | null>(null);
	const [isApproving, setIsApproving] = useState(false);
	const [isRejecting, setIsRejecting] = useState(false);

	// Debounce search
	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search), 300);
		return () => clearTimeout(timer);
	}, [search]);

	// Fetch TCRs
	const fetchTcrs = useCallback(async () => {
		if (!selectedLocationId) return;

		setIsLoading(true);
		setError(null);
		try {
			const response = await providerProfileApi.getTcrApprovals({
				locationId: selectedLocationId,
				status: statusFilter,
				search: debouncedSearch || undefined,
				staffId: staffFilter || undefined,
				dateFrom: dateFrom || undefined,
				dateTo: dateTo || undefined,
			});
			setData(response);
			// Preserve staff options from the first load (they don't change with filters)
			if (response.staffOptions?.length) {
				setStaffOptions(response.staffOptions);
			}
		} catch (err) {
			const apiError = err as ApiError;
			setError(apiError.message || 'Failed to load TCR approvals');
		} finally {
			setIsLoading(false);
		}
	}, [selectedLocationId, statusFilter, debouncedSearch, staffFilter, dateFrom, dateTo]);

	useEffect(() => {
		fetchTcrs();
	}, [fetchTcrs]);

	// Handle approve
	const handleApprove = async (id: number, bonus: number, adjustment: number) => {
		setIsApproving(true);
		try {
			await providerProfileApi.approveTcr(id, bonus, adjustment);
			toast.success('TCR approved successfully!');
			setSelectedTcr(null);
			fetchTcrs();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to approve TCR');
		} finally {
			setIsApproving(false);
		}
	};

	// Handle reject
	const handleReject = async (id: number, reviewNotes: string) => {
		setIsRejecting(true);
		try {
			await providerProfileApi.rejectTcr(id, reviewNotes);
			toast.success('TCR rejected. Staff has been notified.');
			setSelectedTcr(null);
			fetchTcrs();
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to reject TCR');
		} finally {
			setIsRejecting(false);
		}
	};

	const tcrs = data?.tcrs ?? [];

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-gray-900 dark:text-white">TCR Approvals</h1>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					Review and approve staff task completion reports
				</p>
			</div>

			{/* Stats Row */}
			{data && (
				<div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
					<StatCard
						value={data.pendingReview}
						label="Pending Review"
						borderColor="border-l-amber-500"
						iconBg="bg-amber-100 dark:bg-amber-900/40"
						icon={<ClockIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />}
					/>
					<StatCard
						value={data.approvedThisMonth}
						label="Approved This Month"
						borderColor="border-l-emerald-500"
						iconBg="bg-emerald-100 dark:bg-emerald-900/40"
						icon={
							<CheckCircleIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
						}
					/>
					<StatCard
						value={data.rejectedCount}
						label="Rejected"
						borderColor="border-l-red-500"
						iconBg="bg-red-100 dark:bg-red-900/40"
						icon={<XCircleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />}
					/>
					<StatCard
						value={formatCurrency(data.pendingPayout)}
						label="Pending Payout"
						borderColor="border-l-[#667eea]"
						iconBg="bg-indigo-100 dark:bg-indigo-900/40"
						icon={<DocumentTextIcon className="w-6 h-6 text-[#667eea]" />}
					/>
				</div>
			)}

			{/* Filter Bar */}
			<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 mb-6 flex flex-wrap gap-3 items-end">
				<div className="relative flex-1 min-w-[200px]">
					<MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
					<input
						type="text"
						placeholder="Search by staff or client..."
						value={search}
						onChange={e => setSearch(e.target.value)}
						className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-[#667eea] transition-colors"
					/>
				</div>
				<select
					value={statusFilter}
					onChange={e => setStatusFilter(e.target.value)}
					className="px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] cursor-pointer min-w-[150px]"
				>
					{STATUS_OPTIONS.map(opt => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</select>
				<select
					value={staffFilter}
					onChange={e => setStaffFilter(e.target.value)}
					className="px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] cursor-pointer min-w-[150px]"
				>
					<option value="">All Staff</option>
					{staffOptions.map(s => (
						<option key={s.id} value={String(s.id)}>
							{s.name}
						</option>
					))}
				</select>
				<div className="flex flex-col gap-1">
					<label className="text-[0.65rem] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
						Service Date From
					</label>
					<input
						type="date"
						value={dateFrom}
						onChange={e => setDateFrom(e.target.value)}
						max={dateTo || undefined}
						className="px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] min-w-[150px]"
					/>
				</div>
				<div className="flex flex-col gap-1">
					<label className="text-[0.65rem] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">
						Service Date To
					</label>
					<input
						type="date"
						value={dateTo}
						onChange={e => setDateTo(e.target.value)}
						min={dateFrom || undefined}
						className="px-4 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-[#667eea] min-w-[150px]"
					/>
				</div>
				{(dateFrom || dateTo) && (
					<button
						type="button"
						onClick={() => {
							setDateFrom('');
							setDateTo('');
						}}
						className="text-xs font-semibold text-red-500 hover:text-red-600 underline self-end pb-2"
					>
						Clear dates
					</button>
				)}
			</div>

			{/* Content */}
			{isLoading && !data ? (
				<AppLoader message="Loading TCR approvals" variant="inline" />
			) : error ? (
				<Alert
					type="warning"
					icon={<ExclamationTriangleIcon className="w-8 h-8" />}
					title="Unable to Load TCR Approvals"
					message={error}
					actions={[{ label: 'Retry', onClick: fetchTcrs, primary: true }]}
					onDismiss={() => setError(null)}
				/>
			) : !isLoading && tcrs.length === 0 ? (
				<div className="text-center py-16">
					<DocumentTextIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
					<h3 className="text-lg font-semibold text-gray-500 dark:text-gray-400 mb-1">
						No reports found
					</h3>
					<p className="text-sm text-gray-400 dark:text-gray-500">
						{debouncedSearch ||
						statusFilter !== 'ALL' ||
						staffFilter ||
						dateFrom ||
						dateTo
							? 'Try adjusting your search or filters'
							: 'Staff task completion reports will appear here'}
					</p>
				</div>
			) : (
				<>
					{/* Loading indicator for filter changes */}
					{isLoading && (
						<div className="flex justify-center py-4">
							<Spinner size="md" className="text-[#667eea]" />
						</div>
					)}

					{/* TCR Cards */}
					<div className="flex flex-col gap-4">
						{tcrs.map(tcr => {
							const dateParts = getDateParts(tcr.appointmentDateTime);
							const style = getStatusStyle(tcr.status);

							return (
								<div
									key={tcr.id}
									onClick={() => setSelectedTcr(tcr)}
									className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 cursor-pointer border-l-4 ${style.border} hover:shadow-md hover:-translate-y-0.5 transition-all`}
								>
									{/* Card Header: Staff Info + Status */}
									<div className="flex items-center justify-between mb-3">
										<div className="flex items-center gap-3">
											<div
												className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm"
												style={{
													background:
														'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
												}}
											>
												{tcr.staffInitials}
											</div>
											<div>
												<p className="font-semibold text-gray-900 dark:text-white">
													{tcr.staffName}
												</p>
												<p className="text-xs text-gray-500 dark:text-gray-400">
													{tcr.staffRole}
												</p>
											</div>
										</div>
										<span
											className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${style.badge}`}
										>
											{tcr.status}
										</span>
									</div>

									{/* Card Body */}
									<div className="flex items-center gap-4 pt-3 border-t border-gray-100 dark:border-gray-700">
										{/* Date Block */}
										<div className="text-center min-w-[60px] py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hidden sm:block">
											<p className="text-xl font-bold text-[#667eea] leading-none">
												{dateParts.day}
											</p>
											<p className="text-xs text-gray-500 dark:text-gray-400 uppercase mt-1">
												{dateParts.month}
											</p>
										</div>

										{/* Service Info */}
										<div className="flex-1 min-w-0">
											<p className="font-medium text-gray-900 dark:text-white truncate">
												{tcr.serviceName}
											</p>
											<p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
												{tcr.size ? `${tcr.size}` : ''}
												{tcr.hairLength ? ` \u00B7 ${tcr.hairLength}` : ''}
												{tcr.durationMinutes
													? ` \u00B7 ${tcr.durationMinutes} mins`
													: ''}
											</p>
											<p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
												Client: {tcr.customerName}
											</p>
											{tcr.staffAssignedTasks &&
												tcr.staffAssignedTasks.length > 0 && (
													<div className="flex flex-wrap gap-1.5 mt-2">
														{tcr.staffAssignedTasks.map((task, idx) => (
															<span
																key={idx}
																className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white"
															>
																{task}
															</span>
														))}
													</div>
												)}
										</div>

										{/* Right: Commission (base + bonus - adjustment) + Date */}
										{(() => {
											const bonus = tcr.bonus ?? 0;
											const adjustment = tcr.adjustment ?? 0;
											const totalCommission =
												tcr.totalEarnings + bonus - adjustment;
											const hasAdjustments = bonus > 0 || adjustment > 0;
											return (
												<div className="text-right min-w-[110px] flex-shrink-0">
													<p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-0.5">
														Commission
													</p>
													<p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
														{formatCurrency(totalCommission)}
													</p>
													{hasAdjustments && (
														<div className="flex flex-wrap justify-end items-center gap-1 mt-1.5">
															{bonus > 0 && (
																<span className="px-1.5 py-0.5 rounded-md text-[11px] font-semibold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400">
																	+{formatCurrency(bonus)} bonus
																</span>
															)}
															{adjustment > 0 && (
																<span className="px-1.5 py-0.5 rounded-md text-[11px] font-semibold bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400">
																	&minus;{formatCurrency(adjustment)} adj
																</span>
															)}
														</div>
													)}
													<p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
														Submitted {formatDate(tcr.submittedAt)}
													</p>
												</div>
											);
										})()}
									</div>
								</div>
							);
						})}
					</div>
				</>
			)}

			{/* Detail Modal */}
			<TCRDetailModal
				tcr={selectedTcr}
				onClose={() => setSelectedTcr(null)}
				onApprove={handleApprove}
				onReject={handleReject}
				isApproving={isApproving}
				isRejecting={isRejecting}
			/>
		</AppLayout>
	);
};

export default ProviderTCRApprovals;
